// Parse a server-sent-events stream into typed events.
//
// Events look like: `event: <name>\ndata: <json>\n\n` (the backend emits this format).
// Yields one decoded event at a time.

export interface SseEvent<T = any> {
  event: string;
  data: T;
}

export async function* parseSseStream<T = any>(
  response: Response,
): AsyncGenerator<SseEvent<T>, void, unknown> {
  if (!response.body) {
    throw new Error("No response body for SSE stream");
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Events are separated by a blank line (\n\n).
    let separatorIdx: number;
    while ((separatorIdx = buffer.indexOf("\n\n")) !== -1) {
      const rawEvent = buffer.slice(0, separatorIdx);
      buffer = buffer.slice(separatorIdx + 2);
      const ev = parseEventBlock<T>(rawEvent);
      if (ev) yield ev;
    }
  }
  // Flush any final event without trailing newline
  if (buffer.trim()) {
    const ev = parseEventBlock<T>(buffer);
    if (ev) yield ev;
  }
}

function parseEventBlock<T>(block: string): SseEvent<T> | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const rawLine of block.split("\n")) {
    const line = rawLine.replace(/\r$/, "");
    if (!line) continue;
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim());
    }
  }
  if (!dataLines.length) return null;
  const dataStr = dataLines.join("\n");
  let parsed: any;
  try {
    parsed = JSON.parse(dataStr);
  } catch {
    parsed = dataStr;
  }
  return { event, data: parsed };
}
